if (!customElements.get('numeric-range-filter')) {
  class NumericRangeFilter extends HTMLElement {
    connectedCallback() {
      this.inputMin = this.querySelector('.cc-price-range__input--min');
      this.inputMax = this.querySelector('.cc-price-range__input--max');
      this.controlMin = this.querySelector('.cc-price-range__control--min');
      this.controlMax = this.querySelector('.cc-price-range__control--max');
      this.bar = this.querySelector('.cc-price-range__bar');
      this.activeBar = this.querySelector('.cc-price-range__bar-active');
      this.checkboxEls = [...this.querySelectorAll('input[type="checkbox"]')];

      this.snapValues = this.checkboxEls
        .map(cb => parseFloat(cb.dataset.numericValue))
        .filter(v => !isNaN(v))
        .sort((a, b) => a - b);

      if (this.snapValues.length === 0) return;

      this.valueMin = this.snapValues[0];
      this.valueMax = this.snapValues[this.snapValues.length - 1];
      this.valueRange = this.valueMax - this.valueMin;

      const checked = this.checkboxEls
        .filter(cb => cb.checked)
        .map(cb => parseFloat(cb.dataset.numericValue));

      this.controls = {
        min: {
          barControl: this.controlMin,
          input: this.inputMin,
          value: checked.length > 0 ? Math.min(...checked) : this.valueMin
        },
        max: {
          barControl: this.controlMax,
          input: this.inputMax,
          value: checked.length > 0 ? Math.max(...checked) : this.valueMax
        }
      };

      this.inputMin.value = this.controls.min.value;
      this.inputMax.value = this.controls.max.value;
      this.inputMin.placeholder = this.valueMin;
      this.inputMax.placeholder = this.valueMax;
      this.inputMin.min = this.inputMax.min = this.valueMin;
      this.inputMin.max = this.inputMax.max = this.valueMax;

      [this.controlMin, this.controlMax].forEach(ctrl => {
        ctrl.setAttribute('role', 'slider');
        ctrl.setAttribute('tabindex', '0');
        ctrl.setAttribute('aria-valuemin', this.valueMin);
        ctrl.setAttribute('aria-valuemax', this.valueMax);
      });
      this.controlMin.setAttribute('aria-valuenow', this.controls.min.value);
      this.controlMax.setAttribute('aria-valuenow', this.controls.max.value);

      this.rtl = document.documentElement.getAttribute('dir') === 'rtl';
      this.bindEvents();
      this.render();
    }

    snapToNearest(value) {
      return this.snapValues.reduce((prev, curr) =>
        Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
      );
    }

    getPxToValueRatio() {
      const w = this.bar.clientWidth;
      if (w === 0 || this.valueRange === 0) return 1;
      const r = w / this.valueRange;
      return this.rtl ? -r : r;
    }

    setControlValue(control, rawValue) {
      let value = parseFloat(rawValue);
      if (isNaN(value)) return;

      if (control === this.controls.min) {
        value = Math.max(this.valueMin, Math.min(value, this.controls.max.value));
      } else {
        value = Math.min(this.valueMax, Math.max(value, this.controls.min.value));
      }

      value = this.snapToNearest(value);
      if (control.value === value) return;

      control.value = value;
      if (parseFloat(control.input.value) !== value) {
        control.input.value = value;
        control.input.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          detail: { sender: 'theme:numeric_range_filter' }
        }));
      }
      control.barControl.setAttribute('aria-valuenow', value);
      this.updateCheckboxes();
    }

    updateCheckboxes() {
      const min = this.controls.min.value;
      const max = this.controls.max.value;
      const isFullRange = min <= this.valueMin && max >= this.valueMax;
      this.checkboxEls.forEach(cb => {
        const v = parseFloat(cb.dataset.numericValue);
        cb.checked = !isFullRange && v >= min && v <= max;
      });
    }

    render() {
      const pcMin = this.valueRange === 0 ? 0 : (this.controls.min.value - this.valueMin) / this.valueRange * 100;
      const pcMax = this.valueRange === 0 ? 100 : (this.controls.max.value - this.valueMin) / this.valueRange * 100;
      if (this.rtl) {
        this.controlMin.style.right = `${pcMin}%`;
        this.controlMax.style.right = `${pcMax}%`;
        this.activeBar.style.right = `${pcMin}%`;
        this.activeBar.style.left = `${100 - pcMax}%`;
      } else {
        this.controlMin.style.left = `${pcMin}%`;
        this.controlMax.style.left = `${pcMax}%`;
        this.activeBar.style.left = `${pcMin}%`;
        this.activeBar.style.right = `${100 - pcMax}%`;
      }
    }

    bindEvents() {
      [this.controlMin, this.controlMax].forEach(ctrl => {
        ctrl.addEventListener('mousedown', e => {
          e.preventDefault();
          const startX = e.clientX;
          const ac = ctrl === this.controlMin ? this.controls.min : this.controls.max;
          const startVal = ac.value;
          this.activeControl = ac;
          const move = e => {
            this.setControlValue(ac, startVal + (e.clientX - startX) / this.getPxToValueRatio());
            this.render();
          };
          const up = e => {
            e.preventDefault();
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
          };
          window.addEventListener('mousemove', move);
          window.addEventListener('mouseup', up);
        });

        ctrl.addEventListener('touchstart', e => {
          e.preventDefault();
          const startX = e.touches[0].clientX;
          const ac = ctrl === this.controlMin ? this.controls.min : this.controls.max;
          const startVal = ac.value;
          this.activeControl = ac;
          const move = e => {
            this.setControlValue(ac, startVal + (e.touches[0].clientX - startX) / this.getPxToValueRatio());
            this.render();
          };
          const end = e => {
            e.preventDefault();
            window.removeEventListener('touchmove', move);
            window.removeEventListener('touchend', end);
          };
          window.addEventListener('touchmove', move, { passive: true });
          window.addEventListener('touchend', end);
        }, { passive: false });

        ctrl.addEventListener('keydown', e => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
          e.preventDefault();
          const ac = ctrl === this.controlMin ? this.controls.min : this.controls.max;
          this.activeControl = ac;
          const idx = this.snapValues.indexOf(ac.value);
          let ni = idx;
          if (e.key === 'ArrowRight') ni = Math.min(idx + 1, this.snapValues.length - 1);
          else if (e.key === 'ArrowLeft') ni = Math.max(idx - 1, 0);
          else if (e.key === 'Home') ni = 0;
          else if (e.key === 'End') ni = this.snapValues.length - 1;
          this.setControlValue(ac, this.snapValues[ni]);
          this.render();
        });
      });

      [this.inputMin, this.inputMax].forEach(input => {
        input.addEventListener('change', e => {
          if (e.detail && e.detail.sender === 'theme:numeric_range_filter') return;
          const ctrl = input === this.inputMin ? this.controls.min : this.controls.max;
          this.activeControl = ctrl;
          this.setControlValue(ctrl, parseFloat(e.target.value));
          this.render();
        });
      });
    }
  }

  customElements.define('numeric-range-filter', NumericRangeFilter);
}
